import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Copy, KeyRound, PlugZap, RotateCw } from "lucide-react";

interface Connection {
  id: string;
  name: string;
  wstoken: string;
  baseUrl: string | null;
  active: boolean;
  defaultGrade: number | null;
  autoActivateSubscription: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

interface ListResponse {
  success: true;
  data: Connection[];
}

interface CreateResponse {
  success: true;
  data: Connection;
}

const WEBSERVICE_URL = "/api/openemis/webservice/rest/server.php";

function shortToken(token: string): string {
  if (!token) return "";
  if (token.length <= 12) return token;
  return `${token.slice(0, 6)}…${token.slice(-4)}`;
}

export default function AdminOpenemis() {
  const { toast } = useToast();
  const [freshToken, setFreshToken] = useState<{ id: string; token: string } | null>(null);

  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [defaultGrade, setDefaultGrade] = useState<string>("6");
  const [autoActivate, setAutoActivate] = useState(false);

  const listQuery = useQuery<ListResponse>({
    queryKey: ["/api/admin/openemis/connections"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/openemis/connections");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name };
      if (baseUrl.trim()) body.baseUrl = baseUrl.trim();
      if (defaultGrade) body.defaultGrade = parseInt(defaultGrade, 10);
      if (autoActivate) body.autoActivateSubscription = true;
      const res = await apiRequest("POST", "/api/admin/openemis/connections", body);
      return res.json() as Promise<CreateResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/openemis/connections"] });
      setFreshToken({ id: data.data.id, token: data.data.wstoken });
      setName("");
      setBaseUrl("");
      setDefaultGrade("6");
      setAutoActivate(false);
      toast({ title: "Connection created", description: "Copy the token below. It stays visible for admins but paste it into OpenEMIS now." });
    },
    onError: (err: any) => toast({ title: "Failed to create", description: err.message, variant: "destructive" }),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/openemis/connections/${id}/active`, { active });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/openemis/connections"] }),
    onError: (err: any) => toast({ title: "Failed to update", description: err.message, variant: "destructive" }),
  });

  const rotateToken = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/openemis/connections/${id}/rotate-token`, {});
      return res.json() as Promise<CreateResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/openemis/connections"] });
      setFreshToken({ id: data.data.id, token: data.data.wstoken });
      toast({ title: "Token rotated", description: "Paste the new token into OpenEMIS immediately. The previous token is now invalid." });
    },
    onError: (err: any) => toast({ title: "Failed to rotate", description: err.message, variant: "destructive" }),
  });

  const copy = (value: string, label = "Copied") => {
    navigator.clipboard.writeText(value);
    toast({ title: label });
  };

  const webserviceAbsolute = `${window.location.origin}${WEBSERVICE_URL}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <PlugZap className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">OpenEMIS Bridge</h1>
          <p className="text-sm text-muted-foreground">
            Academy speaks the Moodle web-service protocol so any OpenEMIS instance can auto-provision students. Give a Ministry (or a school) admin the URL below plus a wstoken and their existing "External Data Source - LMS" adapter starts pushing users into Academy.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>OpenEMIS-side setup</CardTitle>
          <CardDescription>Paste these two values into the OpenEMIS "External Data Source - LMS" configuration.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>Base URL</Label>
            <div className="flex gap-2">
              <Input readOnly value={webserviceAbsolute.replace(WEBSERVICE_URL, "/api/openemis")} />
              <Button size="icon" variant="outline" onClick={() => copy(webserviceAbsolute.replace(WEBSERVICE_URL, "/api/openemis"), "Base URL copied")}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              OpenEMIS appends <code>/webservice/rest/server.php</code> automatically. Full endpoint: <code>{webserviceAbsolute}</code>
            </p>
          </div>
          <div className="space-y-1">
            <Label>API Token</Label>
            <p className="text-xs text-muted-foreground">Generate one below and paste the returned wstoken into OpenEMIS as the "api_token" attribute.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create a new connection</CardTitle>
          <CardDescription>One per OpenEMIS instance you want to accept pushes from.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="oe-name">Name</Label>
              <Input id="oe-name" placeholder="e.g. Jamaica MOE, Greater Portmore OpenEMIS" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="oe-base">OpenEMIS instance URL (optional)</Label>
              <Input id="oe-base" placeholder="https://jm-moe.openemis.org" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="oe-grade">Default grade for provisioned students</Label>
              <Input id="oe-grade" type="number" min={1} max={13} value={defaultGrade} onChange={(e) => setDefaultGrade(e.target.value)} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch id="oe-auto" checked={autoActivate} onCheckedChange={setAutoActivate} />
              <Label htmlFor="oe-auto">Auto-activate subscription on provision</Label>
            </div>
          </div>
          <Button onClick={() => createMutation.mutate()} disabled={!name || createMutation.isPending}>
            {createMutation.isPending ? "Creating…" : "Create connection"}
          </Button>
        </CardContent>
      </Card>

      {freshToken && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Copy this wstoken into OpenEMIS
            </CardTitle>
            <CardDescription>Paste it under the "api_token" attribute in OpenEMIS's External Data Source - LMS config.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={freshToken.token} className="font-mono text-xs" />
              <Button size="icon" variant="outline" onClick={() => copy(freshToken.token, "Token copied")}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setFreshToken(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active connections</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : listQuery.data?.data?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Default grade</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listQuery.data.data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      {c.baseUrl && <div className="text-xs text-muted-foreground">{c.baseUrl}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{shortToken(c.wstoken)}</TableCell>
                    <TableCell>{c.defaultGrade ?? <span className="text-muted-foreground">6 (default)</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : <Badge variant="outline">never</Badge>}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c.active}
                        onCheckedChange={(v) => toggleActive.mutate({ id: c.id, active: v })}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm("Rotate wstoken? The current token stops working immediately and the new one must be pasted into OpenEMIS.")) {
                            rotateToken.mutate(c.id);
                          }
                        }}
                      >
                        <RotateCw className="h-3 w-3 mr-1" /> Rotate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No connections yet. Create one above to hand off to a Ministry / school admin.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
